// ============================================================================
// 1. 임포트 및 전역 설정 (Imports & Config)
// ============================================================================
import React, { useMemo, useRef, useState, useEffect } from 'react'; // 🚨 [추가] useEffect 추가
import { Contact, SalesRep, Deal } from '../types';
import { 
  Search, Upload, Database, 
  ArrowUpDown, Filter, ShieldCheck, Download, Plus, Edit2, Trash2, Check, X
} from 'lucide-react';
import { crmDataService } from '../services/crmDataService';
import * as XLSX from 'xlsx';
import PermissionGate from './PermissionGate'; // 🚨 [유지] 마법 상자 불러오기

interface ContactListProps {
  currentUser?: any; // 🚨 [추가] 권한 체크용
  contacts: Contact[];
  salesReps: SalesRep[]; 
  deals: Deal[];
  onUpdateContacts: (contacts: Contact[]) => void;
  globalRefreshKey?: number;
}

// 🚀 [수정] 정렬 기준 최적화: No(순번)는 클라이언트 계산이므로 제외, 핵심 컬럼 위주 구성
type SortField = 'company' | 'region' | 'category' | 'department' | 'contactPerson' | 'phone' | 'salesRep' | 'recentYear' | 'grade';
type SortOrder = 'asc' | 'desc';

interface ContactForm {
  company: string;
  businessNumber: string; 
  region: string;         // 🚨 [추가] 지역
  category: string;       // 🚨 [추가] 구분
  department: string;     // 🚨 [추가] 학과
  name: string;           // 대표자
  contactPerson: string;  // 🚨 [추가] 거래처 담당자
  email: string;          
  phone: string;
  salesRep: string;
  recentYear: number;     // 🚨 [추가] 등록 연도 (강제 고정용)
}

// 🚨 [추가] 컴포넌트 선언부에 currentUser 추가
const ContactList: React.FC<ContactListProps> = ({ currentUser, contacts, salesReps, deals, onUpdateContacts, globalRefreshKey = 0 }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // 🚨 [추가] 실제 DB에 가입된 직원(유저) 목록을 담을 그릇
  const [dbUsers, setDbUsers] = useState<any[]>([]);

  // 🚨 [추가] 화면이 켜질 때 진짜 유저 목록을 백엔드에서 가져옵니다.
  useEffect(() => {
    let mounted = true;
    crmDataService.getAllUsers().then(users => {
      if (mounted) setDbUsers(users || []);
    });
    return () => { mounted = false; };
  }, [globalRefreshKey]); 

  const [newContact, setNewContact] = useState<ContactForm>({
    company: '',
    businessNumber: '', 
    region: '',           // 🚨 [추가]
    category: '',       
    department: '',     
    name: '',
    contactPerson: '',  
    email: '',          
    phone: '',
    salesRep: '미정',
    recentYear: new Date().getFullYear() // 🚨 [추가] 올해 연도로 기본값 고정
  });

  const [editForm, setEditForm] = useState<ContactForm>({
    company: '',
    businessNumber: '', 
    region: '',           // 🚨 [추가]
    category: '',       
    department: '',     
    name: '',
    contactPerson: '',  
    email: '',          
    phone: '',
    salesRep: '미정',
    recentYear: new Date().getFullYear() // 🚨 [추가] 올해 연도로 기본값 고정
  });
  
  // 📅 [핵심] 연도 필터 상태
  const [selectedYear, setSelectedYear] = useState<string>('All');
  
  // 🚨 [디자인 수정] 회원님 요청에 따른 핵심 7개 컬럼 + 자사영업 배치 너비 설정
  const [colWidths, setColWidths] = useState<Record<string, number>>({
    no: 50,           // 순번
    region: 80,       // 지역
    category: 90,     // 구분
    company: 180,     // 거래처명
    department: 130,  // 학과
    contactPerson: 110, // 거래처 담당자
    phone: 130,       // 연락처
    salesRep: 100,    // 자사 영업
    grade: 60,        // 등급
    manage: 100       // 관리(수정/삭제)
  });

  const resizingRef = useRef<{ field: string; startX: number; startWidth: number } | null>(null);
  
  const [sortField, setSortField] = useState<SortField>('recentYear');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // 리사이징 로직 (유지)
  const startResizing = (field: string, e: React.MouseEvent) => {
    e.preventDefault();
    resizingRef.current = { field, startX: e.pageX, startWidth: colWidths[field] };
    document.addEventListener('mousemove', handleResizing);
    document.addEventListener('mouseup', stopResizing);
  };

  const handleResizing = (e: MouseEvent) => {
    if (!resizingRef.current) return;
    const { field, startX, startWidth } = resizingRef.current;
    const diff = e.pageX - startX;
    setColWidths(prev => ({ ...prev, [field]: Math.max(40, startWidth + diff) }));
  };

  const stopResizing = () => {
    resizingRef.current = null;
    document.removeEventListener('mousemove', handleResizing);
    document.removeEventListener('mouseup', stopResizing);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // 🚨 [핵심 수정] 수십 명 다 나오는 것 방지! (국내팀, 해외팀만 쏙 뽑아냅니다)
  const repOptions = useMemo(() => {
    if (!dbUsers || dbUsers.length === 0) return ['미정', '기타'];
    
    // DB 유저 중에서 '국내' 또는 '해외' 단어가 팀 이름에 들어간 사람만 추출
    const targetUsers = dbUsers.filter((u: any) => {
      const tName = u.team_name || u.team || '';
      return tName.includes('국내') || tName.includes('해외') || tName.includes('영업'); // 영업 포함
    }).map((u: any) => u.name).filter(Boolean);

    // 중복 제거 후 '미정', '기타'를 앞뒤로 배치하여 깔끔하게 제공
    const merged = Array.from(new Set(targetUsers));
    return ['미정', ...merged, '기타'];
  }, [dbUsers]);

  // 🔍 [필터링 로직] 회원님 요청 컬럼 위주로 검색 강화
  const filteredContacts = contacts.filter(contact => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = 
      (contact.company || '').toLowerCase().includes(term) ||
      (contact.category || '').toLowerCase().includes(term) || 
      (contact.department || '').toLowerCase().includes(term) || 
      (contact.region || '').toLowerCase().includes(term) || 
      (contact.contactPerson || '').toLowerCase().includes(term) || 
      (contact.phone || '').toLowerCase().includes(term) ||
      (contact.salesRep || '').toLowerCase().includes(term);

    const matchesYear = selectedYear === 'All' || (contact.recentYear === parseInt(selectedYear));
    return matchesSearch && matchesYear;
  });

  // 정렬 로직 (유지)
  const sortedContacts = useMemo(() => {
    if (!sortField) return filteredContacts;
    return [...filteredContacts].sort((a, b) => {
      const aValue = (a as any)[sortField] || '';
      const bValue = (b as any)[sortField] || '';
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredContacts, sortField, sortOrder]);

  const startEdit = (contact: Contact) => {
    setEditingId(contact.id);
    setEditForm({
      company: contact.company || '',
      businessNumber: contact.businessNumber || '',
      region: contact.region || '',       // 🚨 [추가] 수정 모드 시 지역 불러오기
      category: contact.category || '',
      department: contact.department || '',
      name: contact.name || '',
      contactPerson: contact.contactPerson || '',
      email: contact.email || '',
      phone: contact.phone || '',
      salesRep: contact.salesRep || '미정',
      recentYear: contact.recentYear || new Date().getFullYear() // 🚨 [추가]
    });
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (id: string) => {
    if (!editForm.company.trim()) { alert('회사명은 필수입니다.'); return; }
    const previousContacts = contacts;
    const updatedContacts = contacts.map(c =>
      c.id === id ? { ...c, ...editForm } as Contact : c
    );
    onUpdateContacts(updatedContacts);

    const target = updatedContacts.find(c => c.id === id);
    if (target) {
      const ok = await crmDataService.saveContact(target);
      if (!ok) { onUpdateContacts(previousContacts); alert('저장 실패'); return; }
    }
    setEditingId(null);
  };

  const handleDeleteContact = async (id: string) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    const previousContacts = contacts;
    const updatedContacts = contacts.filter(c => c.id !== id);
    onUpdateContacts(updatedContacts);
    const ok = await crmDataService.deleteContact(id);
    if (!ok) { onUpdateContacts(previousContacts); alert('삭제 실패'); }
  };

  const handleAddContact = async () => {
    if (!newContact.company.trim()) { alert('회사명 필수'); return; }
    const now = new Date();
    const created: Contact = {
      ...newContact,
      id: `local_${now.getTime()}`,
      role: '-',
      grade: 'D',
      recentYear: now.getFullYear(),
      status: 'Active'
    };
    const previousContacts = contacts;
    onUpdateContacts([created, ...contacts]);
    const ok = await crmDataService.saveContact(created);
    if (!ok) { onUpdateContacts(previousContacts); alert('추가 실패'); return; }
    setIsAddModalOpen(false);
    // 🚨 폼 초기화 시 지역(region)과 연도(recentYear)도 초기화 포함
    setNewContact({ company: '', businessNumber: '', region: '', category: '', department: '', name: '', contactPerson: '', email: '', phone: '', salesRep: '미정', recentYear: now.getFullYear() });
  };

  const handleExportExcel = () => {
    const rows = sortedContacts.map((c, idx) => ({
      'No.': idx + 1,
      지역: c.region || '',
      구분: c.category || '',
      거래처명: c.company || '',
      학과: c.department || '',
      '거래처 담당자': c.contactPerson || '',
      연락처: c.phone || '',
      '자사 영업': c.salesRep || ''
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
    XLSX.writeFile(wb, `거래처리스트_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleUploadExcel = () => { setUploadFile(null); setIsUploadModalOpen(true); };

  const handleConfirmUpload = async () => {
    if (!uploadFile) return;
    setIsUploading(true);
    const result = await crmDataService.uploadExcelFile('contacts', uploadFile);
    setIsUploading(false);
    if (result?.status === 'success') {
      const fetched = await crmDataService.getContacts(true);
      onUpdateContacts(fetched || []);
      setIsUploadModalOpen(false);
    }
  };

  const handleFinalizeUpload = async () => {
    if (!window.confirm('최종본을 생성할까요?')) return;
    setIsFinalizing(true);
    const result = await crmDataService.finalizeExcelUpload('contacts');
    setIsFinalizing(false);
    if (result?.status === 'success') {
      const fetched = await crmDataService.getContacts(true);
      onUpdateContacts(fetched || []);
    }
  };

  const handleImportFromSheet = async () => {
    setIsSyncing(true);
    const fetched = await crmDataService.getContacts();
    setIsSyncing(false);
    if (fetched) onUpdateContacts(fetched);
  };

  const handleGradeChange = async (id: string, newGrade: string) => {
    const updated = contacts.map(c => c.id === id ? { ...c, grade: newGrade } : c);
    onUpdateContacts(updated);
    const target = updated.find(c => c.id === id);
    if(target) await crmDataService.saveContact(target);
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100" />;
    return <ArrowUpDown size={12} className={`text-blue-600 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />;
  };

  return (
    <div className="h-full flex flex-col animate-fade-in relative pb-10 bg-white">
      {/* 🟢 상단 툴바 (유지) */}
      <div className="flex justify-between items-center p-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-1.5 h-6 bg-blue-600 rounded-full"></div>
          <span className="text-sm font-black text-slate-600 uppercase tracking-widest">
            고객 데이터베이스 ({filteredContacts.length})
          </span>
        </div>
        <div className="flex gap-2">
            {/* 🛡️ 최종 업로드 자물쇠 채우기 */}
            <PermissionGate user={currentUser} action="contacts_edit">
              <button onClick={handleFinalizeUpload} disabled={isFinalizing} className="flex items-center gap-2 px-4 py-2 rounded-xl text-[14px] font-black text-orange-700 bg-blue-50/50 border border-slate-300 hover:bg-orange-100 transition-colors shadow-sm disabled:opacity-50">
                <Database size={16} /> {isFinalizing ? '처리 중...' : '최종 업로드'}
              </button>
            </PermissionGate>

            {/* 🛡️ 파일 업로드 자물쇠 채우기 */}
            <PermissionGate user={currentUser} action="contacts_edit">
              <button onClick={handleUploadExcel} className="flex items-center gap-2 px-4 py-2 rounded-xl text-[14px] font-black text-blue-700 bg-blue-50/50 border border-slate-300 hover:bg-blue-100 transition-colors shadow-sm">
                <Upload size={16} /> 파일 업로드
              </button>
            </PermissionGate>

            <button onClick={handleExportExcel} className="flex items-center gap-2 px-4 py-2 rounded-xl text-[14px] font-black text-emerald-700 bg-blue-50/50 border border-slate-300 hover:bg-emerald-100 transition-colors shadow-sm">
              <Download size={16} /> 엑셀 다운로드
            </button>
            <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="h-10 pl-9 pr-4 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-600 focus:outline-none focus:border-blue-500 cursor-pointer">
                  <option value="All">전체 기간</option>
                  <option value="2026">2026년</option><option value="2025">2025년</option>
                </select>
            </div>
            {/* 🛡️ 데이터 불러오기 자물쇠 채우기 */}
            <PermissionGate user={currentUser} action="contacts_edit">
              <button onClick={handleImportFromSheet} disabled={isSyncing} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all shadow-sm flex items-center gap-2 text-sm">
                <Upload size={16} /> 데이터 불러오기
              </button>
            </PermissionGate>
        </div>
      </div>

      {/* 🟢 검색창 (유지 및 검색 플레이스홀더 최적화) */}
      <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text"
              placeholder="지역, 구분, 거래처, 학과, 담당자 통합 검색..."
              className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 focus:ring-2 focus:ring-blue-100 outline-none shadow-sm text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* 🛡️ 고객 추가 자물쇠 채우기 (에러 지점 해결: role -> user) */}
            <PermissionGate user={currentUser} action="contacts_edit">
              <button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition-all shadow-sm">
                <Plus size={15} /> 추가
              </button>
            </PermissionGate>
          </div>
      </div>
        
      {/* 🟢 메인 테이블 영역 (회원님 요청 컬럼 순서 및 디자인 적용) */}  
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="overflow-x-auto scrollbar-thin flex-1">
          <div className="flex flex-col w-full min-w-full">
            
            {/* 테이블 헤더: No | 지역 | 구분 | 거래처명 | 학과 | 거래처 담당자 | 연락처 | 자사영업 */}
            <div className="flex bg-slate-100 text-[11px] font-black text-slate-600 uppercase tracking-wider border-b border-slate-200 h-10 items-center sticky top-0 z-10 min-w-max whitespace-nowrap">
              <div style={{ width: colWidths.no }} className="px-4 text-center">No.</div>
              
              <div style={{ width: colWidths.region }} className="px-4 relative group flex items-center gap-1 cursor-pointer" onClick={() => handleSort('region')}>
                지역 {renderSortIcon('region')}
                <div onMouseDown={e => startResizing('region', e)} className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 z-10" />
              </div>

              <div style={{ width: colWidths.category }} className="px-4 relative group flex items-center gap-1 cursor-pointer" onClick={() => handleSort('category')}>
                구분 {renderSortIcon('category')}
                <div onMouseDown={e => startResizing('category', e)} className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 z-10" />
              </div>

              <div style={{ width: colWidths.company, flex: 1 }} className="px-4 relative group flex items-center gap-1 cursor-pointer" onClick={() => handleSort('company')}>
                거래처명 {renderSortIcon('company')}
                <div onMouseDown={e => startResizing('company', e)} className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 z-10" />
              </div>

              <div style={{ width: colWidths.department }} className="px-4 relative group flex items-center gap-1 cursor-pointer" onClick={() => handleSort('department')}>
                학과 {renderSortIcon('department')}
                <div onMouseDown={e => startResizing('department', e)} className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 z-10" />
              </div>

              <div style={{ width: colWidths.contactPerson }} className="px-4 relative group flex items-center gap-1 cursor-pointer" onClick={() => handleSort('contactPerson')}>
                거래처 담당자 {renderSortIcon('contactPerson')}
                <div onMouseDown={e => startResizing('contactPerson', e)} className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 z-10" />
              </div>

              <div style={{ width: colWidths.phone }} className="px-4">연락처</div>

              <div style={{ width: colWidths.salesRep }} className="px-4 relative group flex items-center gap-1 cursor-pointer" onClick={() => handleSort('salesRep')}>
                자사영업 {renderSortIcon('salesRep')}
                <div onMouseDown={e => startResizing('salesRep', e)} className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 z-10" />
              </div>

              <div style={{ width: colWidths.manage }} className="px-4 text-center">관리</div>
            </div>

            {/* 테이블 바디 */}
            <div className="flex flex-col flex-1 divide-y divide-slate-100 min-w-max">
              {sortedContacts.map((contact, index) => (
                <div key={contact.id} className="flex h-12 items-center hover:bg-blue-50/30 transition-colors text-[13px] group">
                  
                  {/* 순번: 실시간 index 부여 */}
                  <div style={{ width: colWidths.no }} className="px-4 text-center text-slate-400 font-mono text-[11px]">
                    {index + 1}
                  </div>

                  {/* 지역 (🚨 [수정] 인라인 에디트 추가) */}
                  <div style={{ width: colWidths.region }} className="px-4 text-slate-600 font-bold">
                    {editingId === contact.id ? (
                      <input className="w-full border rounded px-2 py-1 text-xs" value={editForm.region} onChange={(e) => setEditForm({...editForm, region: e.target.value})} />
                    ) : (contact.region || '-')}
                  </div>

                  {/* 구분: 배지 스타일 */}
                  <div style={{ width: colWidths.category }} className="px-4">
                    {editingId === contact.id ? (
                      <input className="w-full border rounded px-2 py-1 text-xs" value={editForm.category} onChange={(e) => setEditForm({...editForm, category: e.target.value})} />
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-black border border-slate-200">
                        {contact.category || '-'}
                      </span>
                    )}
                  </div>

                  {/* 거래처명: 굵게 강조 */}
                  <div style={{ width: colWidths.company, flex: 1 }} className="px-4 font-black text-slate-800 truncate">
                    {editingId === contact.id ? (
                      <input className="w-full border rounded px-2 py-1 text-xs" value={editForm.company} onChange={(e) => setEditForm({...editForm, company: e.target.value})} />
                    ) : contact.company}
                  </div>

                  {/* 학과 */}
                  <div style={{ width: colWidths.department }} className="px-4 text-slate-600 truncate">
                    {editingId === contact.id ? (
                      <input className="w-full border rounded px-2 py-1 text-xs" value={editForm.department} onChange={(e) => setEditForm({...editForm, department: e.target.value})} />
                    ) : (contact.department || '-')}
                  </div>

                  {/* 거래처 담당자 */}
                  <div style={{ width: colWidths.contactPerson }} className="px-4 text-slate-700 font-bold truncate">
                    {editingId === contact.id ? (
                      <input className="w-full border rounded px-2 py-1 text-xs" value={editForm.contactPerson} onChange={(e) => setEditForm({...editForm, contactPerson: e.target.value})} />
                    ) : (contact.contactPerson || '-')}
                  </div>

                  {/* 연락처 */}
                  <div style={{ width: colWidths.phone }} className="px-4 text-blue-700 font-medium">
                    {editingId === contact.id ? (
                      <input className="w-full border rounded px-2 py-1 text-xs" value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} />
                    ) : (contact.phone || '-')}
                  </div>

                  {/* 🚨 자사영업 (인라인 수정 시에도 국내팀+해외팀만 나오도록 적용) */}
                  <div style={{ width: colWidths.salesRep }} className="px-4">
                    {editingId === contact.id ? (
                      <select className="w-full border rounded px-1 py-1 text-xs" value={editForm.salesRep} onChange={(e) => setEditForm({...editForm, salesRep: e.target.value})}>
                        {/* 기존 담당자가 삭제되지 않도록 Set으로 합쳐서 렌더링 */}
                        {Array.from(new Set([...repOptions, editForm.salesRep])).map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    ) : (
                      <span className="text-blue-600 font-black">{contact.salesRep}</span>
                    )}
                  </div>

                  {/* 관리 버튼 (🛡️ 수정/삭제 자물쇠 채우기 및 role -> user 보정) */}
                  <div style={{ width: colWidths.manage }} className="px-4 text-center">
                    {editingId === contact.id ? (
                      <div className="flex justify-center gap-1">
                        <button onClick={() => saveEdit(contact.id)} className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700"><Check size={14} /></button>
                        <button onClick={cancelEdit} className="p-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300"><X size={14} /></button>
                      </div>
                    ) : (
                      <PermissionGate user={currentUser} action="contacts_edit">
                        <div className="flex justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => startEdit(contact)} className="p-1.5 text-slate-500 hover:text-blue-600"><Edit2 size={14} /></button>
                          <button onClick={() => handleDeleteContact(contact.id)} className="p-1.5 text-slate-500 hover:text-red-600"><Trash2 size={14} /></button>
                        </div>
                      </PermissionGate>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 🟢 팝업 모달들 (기존 폼 유지) */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="w-[460px] bg-white rounded-2xl p-6 shadow-2xl">
            <h3 className="text-lg font-bold mb-4">엑셀 파일 업로드</h3>
            <input type="file" accept=".xlsx,.xls" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="w-full text-sm border p-2 rounded-lg" />
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setIsUploadModalOpen(false)} className="px-4 py-2 bg-slate-100 rounded-lg text-sm">취소</button>
              <button onClick={handleConfirmUpload} disabled={isUploading} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold">
                {isUploading ? '업로드 중...' : '추가하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="w-[500px] bg-white rounded-2xl p-6 shadow-2xl overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-bold mb-4 text-slate-800">새 고객 추가</h3>
            
            {/* 🚨 [수정] Grid 레이아웃에 2개씩 깔끔하게 짝지어 배치 */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-[11px] font-bold text-slate-500">거래처명 (필수)</label>
                <input className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={newContact.company} onChange={(e) => setNewContact({...newContact, company: e.target.value})} placeholder="회사명" />
              </div>
              
              {/* 🚨 지역과 구분을 나란히 배치 */}
              <div>
                <label className="text-[11px] font-bold text-slate-500">지역</label>
                <input className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={newContact.region} onChange={(e) => setNewContact({...newContact, region: e.target.value})} placeholder="서울, 경기 등" />
              </div>
              <div>
                <label className="text-[11px] font-bold text-slate-500">구분</label>
                <input className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={newContact.category} onChange={(e) => setNewContact({...newContact, category: e.target.value})} placeholder="대학, 고교 등" />
              </div>

              {/* 학과와 담당자를 나란히 배치 */}
              <div>
                <label className="text-[11px] font-bold text-slate-500">학과/부서</label>
                <input className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={newContact.department} onChange={(e) => setNewContact({...newContact, department: e.target.value})} placeholder="기계과 등" />
              </div>
              <div>
                <label className="text-[11px] font-bold text-slate-500">거래처 담당자</label>
                <input className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={newContact.contactPerson} onChange={(e) => setNewContact({...newContact, contactPerson: e.target.value})} placeholder="실무자 이름" />
              </div>
              
              {/* 연락처와 영업 담당자를 나란히 배치 */}
              <div>
                <label className="text-[11px] font-bold text-slate-500">연락처</label>
                <input className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={newContact.phone} onChange={(e) => setNewContact({...newContact, phone: e.target.value})} placeholder="010-0000-0000" />
              </div>
              <div>
                <label className="text-[11px] font-bold text-slate-500">영업 담당자</label>
                <select className="w-full mt-1 px-3 py-2 border rounded-lg text-sm font-bold text-blue-600" value={newContact.salesRep} onChange={(e) => setNewContact({...newContact, salesRep: e.target.value})}>
                  {/* 🚨 [적용] 모달 추가 창에서도 국내팀+해외팀만 나오게! */}
                  {repOptions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              {/* 🚨 [추가] 등록 연도 (수정 불가) - 아래에 전체 너비로 안정감 있게 배치 */}
              <div className="col-span-2">
                <label className="text-[11px] font-bold text-slate-500">등록 연도 (수정 불가)</label>
                <input 
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-400 cursor-not-allowed font-bold text-center" 
                  value={newContact.recentYear} 
                  readOnly 
                  title="시스템에서 올해 연도를 자동으로 입력합니다."
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2 border-t border-slate-100 pt-4">
              <button onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 bg-slate-100 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-200 transition-colors">취소</button>
              <button onClick={handleAddContact} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95">추가하기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactList;